import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CERT_TYPES, SELLER_TYPES } from "@/lib/constants";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ChevronLeft, ChevronRight, MapPin, Loader2, Sprout, Store, Truck, BadgeCheck, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Farm details", "Address & pin", "Fulfilment", "Certification", "Review"];

export default function SellerRegister() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [geo, setGeo] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [editing, setEditing] = useState(false);

  const [form, setForm] = useState({
    farm_name: "", seller_type: "Farm", owner_full_name: user?.full_name || "", email: user?.email || "", phone: user?.phone || "",
    description: "", photo_url: "",
    address_line1: "", address_line2: "", town: "", county: "", postcode: "",
    collection_available: true, collection_info: "",
    delivery_available: false, delivery_radius_miles: 10, delivery_fee: 0,
    cert_type: "none", cert_body: "", cert_number: "", cert_expiry: "",
  });

  useEffect(() => {
    api.get("/farms/mine").then((res) => {
      if (res.data) {
        const f = res.data;
        setEditing(true);
        const c = f.certifications?.[0] || {};
        setForm((prev) => ({ ...prev,
          farm_name: f.farm_name, seller_type: f.seller_type || "Farm", owner_full_name: f.owner_full_name, email: f.email, phone: f.phone,
          description: f.description, photo_url: f.photo_url,
          address_line1: f.address_line1, address_line2: f.address_line2, town: f.town, county: f.county, postcode: f.postcode,
          collection_available: f.collection_available, collection_info: f.collection_info,
          delivery_available: f.delivery_available, delivery_radius_miles: f.delivery_radius_miles, delivery_fee: f.delivery_fee,
          cert_type: f.cert_type || "none", cert_body: f.cert_body || "", cert_number: f.cert_number || "", cert_expiry: f.cert_expiry || "",
        }));
        setGeo({ lat: f.latitude, lng: f.longitude });
      }
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const usesOrganic = /\borganic\b/i.test(`${form.farm_name} ${form.description}`);
  const isCertOrganic = form.cert_type === "certified_organic" && form.cert_number.trim();
  const organicConflict = usesOrganic && !isCertOrganic;

  const doGeocode = async () => {
    if (!form.postcode.trim()) { toast.error("Enter a postcode"); return; }
    setGeocoding(true);
    try {
      const res = await api.get("/geocode/postcode", { params: { postcode: form.postcode } });
      setGeo(res.data);
      if (!form.town && res.data.town) set("town", res.data.town);
      toast.success("Pin placed on the map!");
    } catch {
      toast.error("Could not find that postcode");
      setGeo(null);
    } finally { setGeocoding(false); }
  };

  const canNext = () => {
    if (step === 0) return form.farm_name && form.owner_full_name && form.email && form.phone;
    if (step === 1) return form.address_line1 && form.town && form.postcode && geo;
    return true;
  };

  const submit = async () => {
    if (organicConflict) {
      toast.error("Remove 'organic' or select Certified Organic with a certificate number.");
      setStep(3);
      return;
    }
    if ((form.cert_type === "certified_organic" || form.cert_type === "other") && !form.cert_number.trim()) {
      toast.error("A certificate number is required for this certification type.");
      setStep(3);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        farm_name: form.farm_name, seller_type: form.seller_type, description: form.description, owner_full_name: form.owner_full_name,
        email: form.email, phone: form.phone, photo_url: form.photo_url,
        address_line1: form.address_line1, address_line2: form.address_line2,
        town: form.town, county: form.county, postcode: form.postcode,
        collection_available: form.collection_available, collection_info: form.collection_info,
        delivery_available: form.delivery_available,
        delivery_radius_miles: Number(form.delivery_radius_miles) || 0,
        delivery_fee: Number(form.delivery_fee) || 0,
        cert_type: form.cert_type,
        cert_body: form.cert_body, cert_number: form.cert_number, cert_expiry: form.cert_expiry,
      };
      await api.post("/farms", payload);
      await refresh();
      toast.success(editing ? "Farm updated!" : "Farm registered! Your pin is now on the map.");
      navigate("/sell/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save farm");
    } finally { setSaving(false); }
  };

  return (
    <div className="pt-16 min-h-screen bg-grain">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-1"><Sprout className="h-5 w-5 text-primary" /><h1 className="text-2xl font-semibold">{editing ? "Edit your farm" : "Set up your farm"}</h1></div>
        <p className="text-muted-foreground text-sm mb-5">Step {step + 1} of {STEPS.length} · {STEPS[step]}</p>
        {!editing && (
          <Card className="rounded-2xl p-4 mb-5 border-primary/30 bg-secondary/40" data-testid="assistant-cta-card">
            <div className="flex items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-[color:var(--fg-wood-600)] text-white grid place-items-center shrink-0"><Sparkles className="h-5 w-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold leading-tight">Prefer not to fill in forms?</div>
                <div className="text-sm text-muted-foreground">Let our assistant set everything up through a quick chat.</div>
              </div>
              <Button type="button" className="shrink-0 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/sell/assistant")} data-testid="assistant-cta-button">
                Let us set it up for you
              </Button>
            </div>
          </Card>
        )}
        <Progress value={((step + 1) / STEPS.length) * 100} className="mb-6 h-2" />

        <Card className="rounded-2xl p-6">
          {step === 0 && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Producer type *</Label>
                <Select value={form.seller_type} onValueChange={(v) => set("seller_type", v)}>
                  <SelectTrigger data-testid="seller-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SELLER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Choose what best describes you. Non-farm producers will name where their key ingredients are sourced on each product.</p>
              </div>
              <div className="space-y-1.5"><Label>Business name *</Label>
                <Input value={form.farm_name} onChange={(e) => set("farm_name", e.target.value)} placeholder="e.g. Meadow Brook Farm, Hillside Bakery" data-testid="seller-setup-farm-name-input" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Owner full name *</Label>
                  <Input value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Phone *</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5"><Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Farm description</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell shoppers about your farm, its story and produce…" rows={4} /></div>
              <div className="space-y-1.5"><Label>Farm photo</Label>
                <ImageUpload value={form.photo_url} onChange={(u) => set("photo_url", u)} label="Upload farm photo" testid="seller-farm-photo" /></div>
            </div>
          )}

          {step === 1 && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Address line 1 *</Label>
                  <Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Address line 2</Label>
                  <Input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Town/City *</Label>
                    <Input value={form.town} onChange={(e) => set("town", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>County</Label>
                    <Input value={form.county} onChange={(e) => set("county", e.target.value)} /></div>
                </div>
                <div className="space-y-1.5"><Label>Postcode *</Label>
                  <div className="flex gap-2">
                    <Input value={form.postcode} onChange={(e) => { set("postcode", e.target.value); setGeo(null); }} placeholder="e.g. BS1 4DJ" data-testid="seller-setup-postcode-input" />
                    <Button type="button" variant="secondary" onClick={doGeocode} disabled={geocoding} data-testid="seller-setup-geocode-button">
                      {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}<span className="ml-1">Locate</span>
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">We use your postcode to place your farm on the map.</p>
                </div>
              </div>
              <div>
                <Card className={`h-full min-h-[220px] rounded-xl grid place-items-center text-center p-6 ${geo ? 'bg-[#eef4e6] border-[#8FB573]' : 'bg-secondary/40'}`} data-testid="seller-setup-pin-preview">
                  {geo ? (
                    <div><div className="mx-auto h-12 w-12 rounded-full bg-primary grid place-items-center text-primary-foreground mb-2"><MapPin className="h-6 w-6" /></div>
                      <p className="font-semibold text-primary">Pin placed!</p>
                      <p className="text-xs text-muted-foreground mt-1">{geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}</p></div>
                  ) : (
                    <div className="text-muted-foreground"><MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Enter postcode and tap Locate<br />to preview your pin</p></div>
                  )}
                </Card>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Store className="h-4 w-4 text-primary" /><Label className="text-base">Collection available</Label></div>
                  <Switch checked={form.collection_available} onCheckedChange={(v) => set("collection_available", v)} data-testid="seller-collection-switch" />
                </div>
                {form.collection_available && (
                  <div className="mt-3 space-y-1.5"><Label>Collection instructions / hours</Label>
                    <Textarea value={form.collection_info} onChange={(e) => set("collection_info", e.target.value)} placeholder="e.g. Collect from farm shop, Mon–Sat 9am–5pm" rows={2} /></div>
                )}
              </div>
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /><Label className="text-base">Delivery available</Label></div>
                  <Switch checked={form.delivery_available} onCheckedChange={(v) => set("delivery_available", v)} data-testid="seller-delivery-switch" />
                </div>
                {form.delivery_available && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Delivery radius (miles)</Label>
                      <Input type="number" value={form.delivery_radius_miles} onChange={(e) => set("delivery_radius_miles", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Delivery fee (£)</Label>
                      <Input type="number" step="0.01" value={form.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} /></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><BadgeCheck className="h-4 w-4" /> Choose your certification. A verified badge is shown on your profile only when a certificate number is provided.</div>
              <div className="space-y-1.5"><Label>Certification status</Label>
                <Select value={form.cert_type} onValueChange={(v) => set("cert_type", v)}>
                  <SelectTrigger data-testid="seller-cert-type-select"><SelectValue placeholder="Select certification" /></SelectTrigger>
                  <SelectContent>{CERT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select></div>
              {(form.cert_type === "certified_organic" || form.cert_type === "other") && (
                <div className="space-y-4">
                  <div className="space-y-1.5"><Label>Certification body {form.cert_type === "other" ? "(e.g. Red Tractor, RSPCA Assured, Pasture for Life, LEAF)" : ""}</Label>
                    <Input value={form.cert_body} onChange={(e) => set("cert_body", e.target.value)} placeholder={form.cert_type === "certified_organic" ? "e.g. Soil Association, OF&G" : "e.g. Red Tractor"} data-testid="seller-cert-body-input" /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>Certificate number *</Label>
                      <Input value={form.cert_number} onChange={(e) => set("cert_number", e.target.value)} data-testid="seller-cert-number-input" /></div>
                    <div className="space-y-1.5"><Label>Expiry date</Label>
                      <Input type="date" value={form.cert_expiry} onChange={(e) => set("cert_expiry", e.target.value)} /></div>
                  </div>
                </div>
              )}
              {organicConflict && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="organic-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>You've used the word "organic" in your farm name or description. In the UK "organic" is legally protected — select <strong>Certified Organic</strong> and enter a valid certificate number, or remove the word.</span>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <h3 className="font-semibold">Review your farm</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Info label="Farm name" value={form.farm_name} />
                <Info label="Owner" value={form.owner_full_name} />
                <Info label="Address" value={[form.address_line1, form.town, form.postcode].filter(Boolean).join(", ")} />
                <Info label="Location" value={geo ? `${geo.lat.toFixed(3)}, ${geo.lng.toFixed(3)}` : "Not set"} />
                <Info label="Collection" value={form.collection_available ? "Yes" : "No"} />
                <Info label="Delivery" value={form.delivery_available ? `Yes (${form.delivery_radius_miles} mi)` : "No"} />
                <Info label="Certification" value={(CERT_TYPES.find((c) => c.value === form.cert_type)?.label.split(" (")[0]) + (form.cert_number ? ` · ${form.cert_number}` : "")} />
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="seller-prev-button">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => { if (canNext()) setStep((s) => s + 1); else toast.error(step === 1 ? "Complete address and tap Locate" : "Fill required fields"); }} data-testid="seller-next-button">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving} className="bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" data-testid="seller-submit-button">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} {editing ? "Save changes" : "Register farm"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="rounded-lg bg-secondary/50 p-3">
    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className="font-medium">{value || "—"}</div>
  </div>
);
